import { relations, sql } from "drizzle-orm";
import {
  boolean,
  check,
  customType,
  date,
  foreignKey,
  index,
  integer,
  jsonb,
  numeric,
  pgEnum,
  pgPolicy,
  pgSchema,
  smallint,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
  vector,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";

export const requestStatus = pgEnum("request_status", [
  "PENDING",
  "SUCCESS",
  "ERROR",
]);
export const equalityOp = pgEnum("equality_op", [
  "eq",
  "neq",
  "lt",
  "lte",
  "gt",
  "gte",
  "in",
]);
export const action = pgEnum("action", [
  "INSERT",
  "UPDATE",
  "DELETE",
  "TRUNCATE",
  "ERROR",
]);
export const factorType = pgEnum("factor_type", ["totp", "webauthn", "phone"]);
export const factorStatus = pgEnum("factor_status", ["unverified", "verified"]);
export const aalLevel = pgEnum("aal_level", ["aal1", "aal2", "aal3"]);
export const codeChallengeMethod = pgEnum("code_challenge_method", [
  "s256",
  "plain",
]);
export const oneTimeTokenType = pgEnum("one_time_token_type", [
  "confirmation_token",
  "reauthentication_token",
  "recovery_token",
  "email_change_token_new",
  "email_change_token_current",
  "phone_change_token",
]);
export const academicSystemType = pgEnum("academic_system_type", [
  "quarter",
  "semester",
]);
export const filesType = pgEnum("files_type", ["document", "lecture-notes"]);
export const quarterType = pgEnum("quarter_type", [
  "fall",
  "winter",
  "spring",
  "summer",
]);
export const semesterType = pgEnum("semester_type", [
  "fall",
  "spring",
  "summer",
]);
export const statusType = pgEnum("status_type", [
  "pending",
  "success",
  "failed",
]);
export const questionType = pgEnum("question_type", [
  "written",
  "multiple_choice",
]);
export const bloomLevel = pgEnum("bloom_level", [
  "recall",
  "comprehension",
  "application",
]);
export const feedbackCategory = pgEnum("feedback_category", [
  "Feature Request",
  "UX / Design",
  "Content Quality",
  "Performance",
  "Other",
]);
export const audioLessonStyleType = pgEnum("audio_lesson_style_type", [
  "podcast",
  "lesson",
]);
export const practiceTestStatus = pgEnum("practice_test_status", [
  "generating",
  "ready",
  "in_progress",
  "grading",
  "completed",
  "failed",
]);

const tsVector = customType<{ data: string }>({
  dataType() {
    return "tsvector";
  },
});

export const _private = pgSchema("private");

export const users = _private.table(
  "users",
  {
    id: uuid().primaryKey().notNull(),
    email: text(),
    firstName: text("first_name"),
    lastName: text("last_name"),
    phoneNumber: text("phone_number"),
    profileImg: text("profile_img"),
    onboardedSteps: smallint("onboarded_steps").default(0).notNull(),
    university: text(),
    studentType: text("student_type"),
    referralSource: text("referral_source"),
    locale: text().default("en").notNull(),
    aiNotetakerOnboarded: boolean("ai_notetaker_onboarded")
      .default(false)
      .notNull(),
    // First-touch attribution captured on the marketing site (sg_attr cookie)
    landingPage: text("landing_page"),
    firstReferrer: text("first_referrer"),
    utmSource: text("utm_source"),
    utmMedium: text("utm_medium"),
    utmCampaign: text("utm_campaign"),
  },
  (table) => [
    unique("users_email_unique").on(table.email),
    unique("unique_phone_number").on(table.phoneNumber),
    pgPolicy("Profiles are viewable by users who created them.", {
      as: "permissive",
      for: "select",
      to: ["public"],
      using: sql`(auth.uid() = id)`,
    }),
    pgPolicy("Users can insert their own profile.", {
      as: "permissive",
      for: "insert",
      to: ["public"],
    }),
    pgPolicy("Users can update own profile.", {
      as: "permissive",
      for: "update",
      to: ["public"],
    }),
  ],
);

export const questionOptions = _private.table(
  "question_options",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    questionId: uuid("question_id").notNull(),
    optionText: text("option_text").notNull(),
    isCorrect: boolean("is_correct").notNull(),
  },
  (table) => [
    index("idx_question_options_question_id").using(
      "btree",
      table.questionId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.questionId],
      foreignColumns: [questions.id],
      name: "question_options_question_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("unique_option_per_question").on(table.questionId, table.optionText),
  ],
);

export const purchases = _private.table(
  "purchases",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    stripeCustomerId: text("stripe_customer_id").notNull(),
    stripeSubscriptionId: text("stripe_subscription_id").notNull(),
    stripePriceId: text("stripe_price_id").notNull(),
    stripeCurrentPeriodEnd: timestamp("stripe_current_period_end", {
      mode: "string",
    }).notNull(),
    isPaused: boolean("is_paused").default(false).notNull(),
    pauseResumesAt: timestamp("pause_resumes_at", {
      withTimezone: true,
      mode: "string",
    }),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_purchases_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "purchases_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("unique_user_id").on(table.userId),
    unique("purchases_stripe_customer_id_key").on(table.stripeCustomerId),
    unique("purchases_stripe_subscription_id_key").on(
      table.stripeSubscriptionId,
    ),
  ],
);

export const studySets = _private.table(
  "study_sets",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: varchar().notNull(),
    userId: uuid("user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    isPublic: boolean("is_public").default(true).notNull(),
    subject: text(),
    description: text(),
    tags: text().array(),
    averageRating: numeric("average_rating").default("0").notNull(),
    totalRatings: integer("total_ratings").default(0).notNull(),
    fts: tsVector("fts").generatedAlwaysAs(
      sql`_private.generate_study_set_fts((name)::text, _private.extract_tags(tags), subject)`,
    ),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("fts_idx").using(
      "gin",
      table.fts.asc().nullsLast().op("tsvector_ops"),
    ),
    index("idx_study_sets_public_created")
      .using(
        "btree",
        table.isPublic.asc().nullsLast().op("timestamptz_ops"),
        table.createdAt.desc().nullsFirst().op("bool_ops"),
      )
      .where(sql`(is_public = true)`),
    index("idx_study_sets_subject_public")
      .using(
        "btree",
        table.subject.asc().nullsLast().op("text_ops"),
        table.isPublic.asc().nullsLast().op("bool_ops"),
      )
      .where(sql`(is_public = true)`),
    index("idx_study_sets_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "study_sets_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const messages = _private.table(
  "messages",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    studySetId: uuid("study_set_id").notNull(),
    parts: jsonb().notNull(),
    role: varchar().notNull(),
    chatSessionId: uuid("chat_session_id").notNull(),
    questionId: uuid("question_id"),
  },
  (table) => [
    index("idx_messages_chat_session_id").using(
      "btree",
      table.chatSessionId.asc().nullsLast().op("uuid_ops"),
    ),
    index("messages_question_id_idx").using(
      "btree",
      table.questionId.asc().nullsLast().op("uuid_ops"),
    ),
    index("messages_study_set_id_idx").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "messages_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.chatSessionId],
      foreignColumns: [chatSessions.id],
      name: "messages_chat_session_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.questionId],
      foreignColumns: [questions.id],
      name: "messages_question_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
  ],
);

export const files = _private.table(
  "files",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    url: text(),
    title: text().notNull(),
    status: statusType("status").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    userId: uuid("user_id").notNull(),
    studySetId: uuid("study_set_id").notNull(),
    storageObjectId: uuid("storage_object_id"),
    failureReason: text("failure_reason"),
    chatStatus: statusType("chat_status"),
    chatFailureReason: text("chat_failure_reason"),
    processedQuizChunks: smallint("processed_quiz_chunks").default(0).notNull(),
    totalQuizChunks: smallint("total_quiz_chunks").default(0).notNull(),
    failedQuizChunks: smallint("failed_quiz_chunks").default(0).notNull(),
    totalUntruncatedQuizChunks: smallint("total_untruncated_quiz_chunks")
      .default(0)
      .notNull(),
    sourceType: text("source_type")
      .default("file")
      .notNull()
      .$type<"youtube" | "plain_text" | "file" | "ai_notes">(),
    textractJobId: text("textract_job_id"),
    textractFeature: text("textract_feature").$type<
      "LAYOUT" | "TEXT_DETECTION"
    >(),
    textractStartedAt: timestamp("textract_started_at", {
      withTimezone: true,
      mode: "string",
    }),
  },
  (table) => [
    index("idx_files_status").using(
      "btree",
      table.status.asc().nullsLast().op("enum_ops"),
    ),
    index("idx_files_textract_job_id").using(
      "btree",
      table.textractJobId.asc().nullsLast().op("text_ops"),
    ),
    index("idx_files_study_set_status").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
      table.status.asc().nullsLast().op("enum_ops"),
    ),
    index("idx_foreign_key_studyset").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_foreign_key_user").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "files_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "files_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    check("files_processed_quiz_chunks_check", sql`processed_quiz_chunks >= 0`),
    check("files_total_quiz_chunks_check", sql`total_quiz_chunks >= 0`),
    check("files_failed_quiz_chunks_check", sql`failed_quiz_chunks >= 0`),
    check("files_total_quiz_chunks_check1", sql`total_quiz_chunks >= 0`),
  ],
);

export const questions = _private.table(
  "questions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    question: text().notNull(),
    answer: text().notNull(),
    type: questionType("type").notNull(),
    studySetId: uuid("study_set_id").notNull(),
    fileId: uuid("file_id"),
    userId: uuid("user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    fts: tsVector("fts").generatedAlwaysAs(
      sql`_private.generate_question_fts(question, answer)`,
    ),
    questionImageUrl: text("question_image_url"),
    answerImageUrl: text("answer_image_url"),
    level: bloomLevel("level"),
    sourceConceptId: uuid("source_concept_id"),
    contentHash: text("content_hash"),
  },
  (table) => [
    index("idx_questions_file_id").using(
      "btree",
      table.fileId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_questions_source_concept_id").using(
      "btree",
      table.sourceConceptId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_questions_study_set_created").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
      table.createdAt.desc().nullsFirst().op("uuid_ops"),
    ),
    index("idx_questions_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_questions_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_questions_user_id_study_set_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("questions_fts_idx").using(
      "gin",
      table.fts.asc().nullsLast().op("tsvector_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "questions_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.fileId],
      foreignColumns: [files.id],
      name: "questions_file_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "questions_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.sourceConceptId],
      foreignColumns: [concepts.id],
      name: "questions_source_concept_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    uniqueIndex("uq_questions_concept_content")
      .on(table.sourceConceptId, table.contentHash)
      .where(sql`source_concept_id IS NOT NULL AND content_hash IS NOT NULL`),
  ],
);

export const concepts = _private.table(
  "concepts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    fileId: uuid("file_id").notNull(),
    startChildChunkId: uuid("start_child_chunk_id").notNull(),
    endChildChunkId: uuid("end_child_chunk_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    displayTitle: text("display_title"),
    conceptGroupId: uuid("concept_group_id"),
    groupDisplayTitle: text("group_display_title"),
  },
  (table) => [
    index("concepts_end_child_chunk_id_idx").using(
      "btree",
      table.endChildChunkId.asc().nullsLast().op("uuid_ops"),
    ),
    index("concepts_file_id_idx").using(
      "btree",
      table.fileId.asc().nullsLast().op("uuid_ops"),
    ),
    index("concepts_start_child_chunk_id_idx").using(
      "btree",
      table.startChildChunkId.asc().nullsLast().op("uuid_ops"),
    ),
    index("concepts_study_set_id_idx").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "concepts_study_set_id_fkey",
    }),
    foreignKey({
      columns: [table.fileId],
      foreignColumns: [files.id],
      name: "concepts_file_id_fkey",
    }),
    foreignKey({
      columns: [table.startChildChunkId],
      foreignColumns: [childChunks.id],
      name: "concepts_start_child_chunk_id_fkey",
    }),
    foreignKey({
      columns: [table.endChildChunkId],
      foreignColumns: [childChunks.id],
      name: "concepts_end_child_chunk_id_fkey",
    }),
  ],
);

export const questionAttempts = _private.table(
  "question_attempts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    questionId: uuid("question_id").notNull(),
    userId: uuid("user_id").notNull(),
    studySetId: uuid("study_set_id").notNull(),
    answerGiven: text("answer_given").notNull(),
    isCorrect: boolean("is_correct").notNull(),
    timestamp: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("question_attempts_question_id_idx").using(
      "btree",
      table.questionId.asc().nullsLast().op("uuid_ops"),
    ),
    index("question_attempts_study_set_id_idx").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.questionId],
      foreignColumns: [questions.id],
      name: "question_attempts_question_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "question_attempts_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "question_attempts_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const userQuestionProgress = _private.table(
  "user_question_progress",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    questionId: uuid("question_id").notNull(),
    ebisuModel: jsonb("ebisu_model").notNull(),
    lastReviewedAt: timestamp("last_reviewed_at", {
      withTimezone: true,
      mode: "string",
    }).notNull(),
    numCorrect: smallint("num_correct").default(0).notNull(),
    numAttempts: smallint("num_attempts").default(0).notNull(),
    consecutiveCorrectAnswers: smallint("consecutive_correct_answers")
      .default(0)
      .notNull(),
  },
  (table) => [
    index("idx_user_question_progress_consecutive").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.consecutiveCorrectAnswers.desc().nullsFirst().op("uuid_ops"),
    ),
    index("idx_user_question_progress_user_id_question_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.questionId.asc().nullsLast().op("uuid_ops"),
    ),
    index("user_question_progress_question_id_idx").using(
      "btree",
      table.questionId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "user_question_progress_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.questionId],
      foreignColumns: [questions.id],
      name: "user_question_progress_question_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("user_question_progress_user_id_question_id_key").on(
      table.userId,
      table.questionId,
    ),
  ],
);

export const userLikes = _private.table(
  "user_likes",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    userId: uuid("user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_likes_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_likes_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "user_likes_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "user_likes_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("unique_user_study_set").on(table.studySetId, table.userId),
  ],
);

export const userActivityCounts = _private.table(
  "user_activity_counts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    generationCount: integer("generation_count").default(0).notNull(),
    chatCount: integer("chat_count").default(0).notNull(),
    lastResetDate: date("last_reset_date"),
    aiExplanationCount: integer("ai_explanation_count").default(0).notNull(),
    autocompletionCount: integer("autocompletion_count").default(0).notNull(),
    aiNotesCount: integer("ai_notes_count").default(0).notNull(),
    audioLessonCount: integer("audio_lesson_count").default(0).notNull(),
    practiceTestCount: integer("practice_test_count").default(0).notNull(),
    voiceQuestionCount: integer("voice_question_count").default(0).notNull(),
  },
  (table) => [
    index("idx_user_activity_counts_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "user_activity_counts_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("user_id_unique").on(table.userId),
    check("check_generation_count_non_negative", sql`generation_count >= 0`),
    check("check_chat_count_non_negative", sql`chat_count >= 0`),
    check(
      "user_activity_counts_ai_explanation_count_check",
      sql`ai_explanation_count >= 0`,
    ),
    check(
      "user_activity_counts_autocompletion_count_check",
      sql`autocompletion_count >= 0`,
    ),
    check(
      "user_activity_counts_ai_notes_count_check",
      sql`ai_notes_count >= 0`,
    ),
    check(
      "user_activity_counts_audio_lesson_count_check",
      sql`audio_lesson_count >= 0`,
    ),
  ],
);

export const studySetRatings = _private.table(
  "study_set_ratings",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    userId: uuid("user_id").notNull(),
    rating: smallint().notNull(),
    feedback: text(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_ratings_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_ratings_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("uniq_user_study_set").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "study_set_ratings_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "study_set_ratings_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const folders = _private.table(
  "folders",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text().notNull(),
    userId: uuid("user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_folders_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "folders_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const studySetsFolders = _private.table(
  "study_sets_folders",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    folderId: uuid("folder_id").notNull(),
    studySetId: uuid("study_set_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_study_sets_folders_folder_id").using(
      "btree",
      table.folderId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_study_sets_folders_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("uniq_study_sets_folders").using(
      "btree",
      table.folderId.asc().nullsLast().op("uuid_ops"),
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.folderId],
      foreignColumns: [folders.id],
      name: "study_sets_folders_folder_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "study_sets_folders_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const chatSessions = _private.table(
  "chat_sessions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    userId: uuid("user_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("chat_sessions_study_set_id_idx").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "chat_sessions_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "chat_sessions_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const parentChunks = _private.table(
  "parent_chunks",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    content: text().notNull(),
    fileId: uuid("file_id").notNull(),
    pageNumber: smallint("page_number"),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    startTime: numeric("start_time"),
    endTime: numeric("end_time"),
  },
  (table) => [
    foreignKey({
      columns: [table.fileId],
      foreignColumns: [files.id],
      name: "parent_chunks_file_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const questionEmbeddings = _private.table(
  "question_embeddings",
  {
    questionId: uuid("question_id").primaryKey().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    embedding: vector({ dimensions: 1536 }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_question_embeddings_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.questionId],
      foreignColumns: [questions.id],
      name: "question_embeddings_question_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const childChunks = _private.table(
  "child_chunks",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    content: text().notNull(),
    embedding: vector({ dimensions: 1536 }),
    pageNumber: smallint("page_number"),
    fileId: uuid("file_id").notNull(),
    studySetId: uuid("study_set_id").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    parentChunkId: uuid("parent_chunk_id").notNull(),
    boundingBox: jsonb("bounding_box"),
    startTime: numeric("start_time"),
    endTime: numeric("end_time"),
    idx: integer(),
  },
  (table) => [
    index().using(
      "btree",
      table.fileId.asc().nullsLast().op("int4_ops"),
      table.idx.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_child_chunks_file_id").using(
      "btree",
      table.fileId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_child_chunks_parent_id").using(
      "btree",
      table.parentChunkId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_child_chunks_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.fileId],
      foreignColumns: [files.id],
      name: "file_sections_file_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.parentChunkId],
      foreignColumns: [parentChunks.id],
      name: "file_sections_parent_chunk_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "file_sections_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);

export const notes = _private.table(
  "notes",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    studySetId: uuid("study_set_id"),
    userId: uuid("user_id").notNull(),
    name: text("name").notNull(),
    isPublic: boolean("is_public").default(true).notNull(),
    userNotes: jsonb("user_notes"),
    aiNotes: jsonb("ai_notes"),
    transcript: text(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_notes_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_notes_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("uniq_notes_study_set_id_user_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "notes_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "notes_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
  ],
);
export const feedback = _private.table(
  "feedback",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    userEmail: text("user_email"),
    category: feedbackCategory("category").notNull(),
    body: text().notNull(),
    rating: smallint(),
    pageUrl: text("page_url"),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_feedback_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "feedback_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    check("feedback_rating_check", sql`rating >= 1 AND rating <= 5`),
  ],
);

export const cancellationFeedback = _private.table(
  "cancellation_feedback",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    reason: text("reason").notNull(),
    outcome: text("outcome")
      .notNull()
      .$type<"canceled" | "paused" | "retained" | "dismissed">(),
    noLongerNeedReason: text("no_longer_need_reason"),
    feedbackText: text("feedback_text"),
    alternativeTool: text("alternative_tool"),
    alternativeReason: text("alternative_reason"),
    rating: smallint(),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_cancellation_feedback_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "cancellation_feedback_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    check(
      "cancellation_feedback_rating_check",
      sql`rating >= 1 AND rating <= 5`,
    ),
  ],
);

export const audioLessons = _private.table(
  "audio_lessons",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    studySetId: uuid("study_set_id").notNull(),
    userId: uuid("user_id").notNull(),
    style: audioLessonStyleType("style").notNull(),
    status: statusType("status").notNull().default("pending"),
    audioUrl: text("audio_url"),
    duration: integer(),
    chapters: jsonb().$type<AudioLessonChapter[]>(),
    transcript: jsonb().$type<AudioLessonTranscriptEntry[]>(),
    script: text(),
    failureReason: text("failure_reason"),
    progressPercent: smallint("progress_percent").default(0).notNull(),
    progressStep: text("progress_step"),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_audio_lessons_study_set_id").using(
      "btree",
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_audio_lessons_user_id").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_audio_lessons_user_study_set").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.studySetId.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "audio_lessons_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "audio_lessons_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    check(
      "audio_lessons_progress_percent_check",
      sql`progress_percent >= 0 AND progress_percent <= 100`,
    ),
  ],
);

export type AudioLessonChapter = {
  label: string;
  startTime: number;
  endTime: number;
};

export type AudioLessonTranscriptEntry = {
  time: number;
  text: string;
  speaker?: string;
};

export const practiceTests = _private.table(
  "practice_tests",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    userId: uuid("user_id").notNull(),
    studySetId: uuid("study_set_id"),
    folderId: uuid("folder_id"),
    status: practiceTestStatus("status").notNull().default("generating"),
    failureReason: text("failure_reason"),
    questionCount: smallint("question_count").notNull(),
    durationSeconds: integer("duration_seconds").notNull(),
    startedAt: timestamp("started_at", { withTimezone: true, mode: "string" }),
    submittedAt: timestamp("submitted_at", {
      withTimezone: true,
      mode: "string",
    }),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_practice_tests_user_study_set").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.studySetId.asc().nullsLast().op("uuid_ops"),
      table.createdAt.desc().nullsFirst().op("timestamptz_ops"),
    ),
    index("idx_practice_tests_user_folder").using(
      "btree",
      table.userId.asc().nullsLast().op("uuid_ops"),
      table.folderId.asc().nullsLast().op("uuid_ops"),
      table.createdAt.desc().nullsFirst().op("timestamptz_ops"),
    ),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id],
      name: "practice_tests_user_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.studySetId],
      foreignColumns: [studySets.id],
      name: "practice_tests_study_set_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.folderId],
      foreignColumns: [folders.id],
      name: "practice_tests_folder_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    check(
      "practice_tests_one_source",
      sql`(study_set_id IS NOT NULL AND folder_id IS NULL) OR (study_set_id IS NULL AND folder_id IS NOT NULL)`,
    ),
  ],
);

export const practiceTestQuestions = _private.table(
  "practice_test_questions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    practiceTestId: uuid("practice_test_id").notNull(),
    orderIndex: smallint("order_index").notNull(),
    conceptName: text("concept_name").notNull(),
    conceptGroupId: uuid("concept_group_id"),
    type: questionType("type").notNull(),
    question: text().notNull(),
    answer: text().notNull(),
    distractors: jsonb().$type<string[]>(),
    answerGiven: text("answer_given"),
    answeredAt: timestamp("answered_at", {
      withTimezone: true,
      mode: "string",
    }),
    isCorrect: boolean("is_correct"),
    feedback: text("feedback"),
    createdAt: timestamp("created_at", { withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_practice_test_questions_test").using(
      "btree",
      table.practiceTestId.asc().nullsLast().op("uuid_ops"),
      table.orderIndex.asc().nullsLast().op("int2_ops"),
    ),
    foreignKey({
      columns: [table.practiceTestId],
      foreignColumns: [practiceTests.id],
      name: "practice_test_questions_practice_test_id_fkey",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    unique("practice_test_questions_unique_order").on(
      table.practiceTestId,
      table.orderIndex,
    ),
  ],
);

// Relations
export const purchasesRelations = relations(purchases, ({ one }) => ({
  usersInPrivate: one(users, {
    fields: [purchases.userId],
    references: [users.id],
  }),
}));

export const usersRelations = relations(users, ({ one, many }) => ({
  purchases: many(purchases),
  questions: many(questions),
  files: many(files),
  studySets: many(studySets),
  questionAttempts: many(questionAttempts),
  userQuestionProgress: many(userQuestionProgress),
  studySetRatings: many(studySetRatings),
  folders: many(folders),
  chatSessions: many(chatSessions),
  notes: many(notes),
  feedback: many(feedback),
  audioLessons: many(audioLessons),
  practiceTests: many(practiceTests),
}));

export const questionOptionsRelations = relations(
  questionOptions,
  ({ one }) => ({
    questions: one(questions, {
      fields: [questionOptions.questionId],
      references: [questions.id],
    }),
  }),
);

export const questionsRelations = relations(questions, ({ one, many }) => ({
  questionOptions: many(questionOptions),
  studySets: one(studySets, {
    fields: [questions.studySetId],
    references: [studySets.id],
  }),
  files: one(files, {
    fields: [questions.fileId],
    references: [files.id],
  }),
  users: one(users, {
    fields: [questions.userId],
    references: [users.id],
  }),
  questionAttempts: many(questionAttempts),
  userQuestionProgress: many(userQuestionProgress),
}));

export const studySetsRelations = relations(studySets, ({ one, many }) => ({
  questions: many(questions),
  files: many(files),
  messages: many(messages),
  users: one(users, {
    fields: [studySets.userId],
    references: [users.id],
  }),
  questionAttempts: many(questionAttempts),
  userLikes: many(userLikes),
  studySetRatings: many(studySetRatings),
  studySetsFolders: many(studySetsFolders),
  parentChunks: many(parentChunks),
  childChunks: many(childChunks),
  chatSessions: many(chatSessions),
  concepts: many(concepts),
  notes: many(notes),
  audioLessons: many(audioLessons),
  practiceTests: many(practiceTests),
}));

export const filesRelations = relations(files, ({ one, many }) => ({
  questions: many(questions),
  studySets: one(studySets, {
    fields: [files.studySetId],
    references: [studySets.id],
  }),
  users: one(users, {
    fields: [files.userId],
    references: [users.id],
  }),
  parentChunks: many(parentChunks),
  childChunks: many(childChunks),
  concepts: many(concepts),
}));

export const messagesRelations = relations(messages, ({ one }) => ({
  studySets: one(studySets, {
    fields: [messages.studySetId],
    references: [studySets.id],
  }),
  chatSessions: one(chatSessions, {
    fields: [messages.chatSessionId],
    references: [chatSessions.id],
  }),
}));

export const chatSessionsRelations = relations(
  chatSessions,
  ({ one, many }) => ({
    messages: many(messages),
    studySets: one(studySets, {
      fields: [chatSessions.studySetId],
      references: [studySets.id],
    }),
    users: one(users, {
      fields: [chatSessions.userId],
      references: [users.id],
    }),
  }),
);

export const questionAttemptsRelations = relations(
  questionAttempts,
  ({ one }) => ({
    questions: one(questions, {
      fields: [questionAttempts.questionId],
      references: [questions.id],
    }),
    users: one(users, {
      fields: [questionAttempts.userId],
      references: [users.id],
    }),
    studySets: one(studySets, {
      fields: [questionAttempts.studySetId],
      references: [studySets.id],
    }),
  }),
);

export const userQuestionProgressRelations = relations(
  userQuestionProgress,
  ({ one }) => ({
    users: one(users, {
      fields: [userQuestionProgress.userId],
      references: [users.id],
    }),
    questions: one(questions, {
      fields: [userQuestionProgress.questionId],
      references: [questions.id],
    }),
  }),
);

export const userLikesRelations = relations(userLikes, ({ one }) => ({
  studySets: one(studySets, {
    fields: [userLikes.studySetId],
    references: [studySets.id],
  }),
  users: one(users, {
    fields: [userLikes.userId],
    references: [users.id],
  }),
}));

export const userActivityCountsRelations = relations(
  userActivityCounts,
  ({ one }) => ({
    users: one(users, {
      fields: [userActivityCounts.userId],
      references: [users.id],
    }),
  }),
);

export const studySetRatingsRelations = relations(
  studySetRatings,
  ({ one }) => ({
    studySets: one(studySets, {
      fields: [studySetRatings.studySetId],
      references: [studySets.id],
    }),
    users: one(users, {
      fields: [studySetRatings.userId],
      references: [users.id],
    }),
  }),
);

export const foldersRelations = relations(folders, ({ one, many }) => ({
  users: one(users, {
    fields: [folders.userId],
    references: [users.id],
  }),
  studySetsFolders: many(studySetsFolders),
  practiceTests: many(practiceTests),
}));

export const studySetsFoldersRelations = relations(
  studySetsFolders,
  ({ one }) => ({
    folders: one(folders, {
      fields: [studySetsFolders.folderId],
      references: [folders.id],
    }),
    studySets: one(studySets, {
      fields: [studySetsFolders.studySetId],
      references: [studySets.id],
    }),
  }),
);

export const childChunksRelations = relations(childChunks, ({ one, many }) => ({
  concepts_startChildChunkId: many(concepts, {
    relationName: "concepts_startChildChunkId_childChunks_id",
  }),
  concepts_endChildChunkId: many(concepts, {
    relationName: "concepts_endChildChunkId_childChunks_id",
  }),
  studySets: one(studySets, {
    fields: [childChunks.studySetId],
    references: [studySets.id],
  }),
  files: one(files, {
    fields: [childChunks.fileId],
    references: [files.id],
  }),
  parentChunks: one(parentChunks, {
    fields: [childChunks.parentChunkId],
    references: [parentChunks.id],
  }),
}));

export const parentChunksRelations = relations(
  parentChunks,
  ({ one, many }) => ({
    files: one(files, {
      fields: [parentChunks.fileId],
      references: [files.id],
    }),
    childChunks: many(childChunks),
  }),
);

export const conceptsRelations = relations(concepts, ({ one }) => ({
  studySets: one(studySets, {
    fields: [concepts.studySetId],
    references: [studySets.id],
  }),
  files: one(files, {
    fields: [concepts.fileId],
    references: [files.id],
  }),
  childChunks_startChildChunkId: one(childChunks, {
    fields: [concepts.startChildChunkId],
    references: [childChunks.id],
    relationName: "concepts_startChildChunkId_childChunks_id",
  }),
  childChunks_endChildChunkId: one(childChunks, {
    fields: [concepts.endChildChunkId],
    references: [childChunks.id],
    relationName: "concepts_endChildChunkId_childChunks_id",
  }),
}));

export const notesRelations = relations(notes, ({ one }) => ({
  studySets: one(studySets, {
    fields: [notes.studySetId],
    references: [studySets.id],
  }),
  users: one(users, {
    fields: [notes.userId],
    references: [users.id],
  }),
}));

export const feedbackRelations = relations(feedback, ({ one }) => ({
  users: one(users, {
    fields: [feedback.userId],
    references: [users.id],
  }),
}));

export const audioLessonsRelations = relations(audioLessons, ({ one }) => ({
  studySets: one(studySets, {
    fields: [audioLessons.studySetId],
    references: [studySets.id],
  }),
  users: one(users, {
    fields: [audioLessons.userId],
    references: [users.id],
  }),
}));

export const practiceTestsRelations = relations(
  practiceTests,
  ({ one, many }) => ({
    users: one(users, {
      fields: [practiceTests.userId],
      references: [users.id],
    }),
    studySets: one(studySets, {
      fields: [practiceTests.studySetId],
      references: [studySets.id],
    }),
    folders: one(folders, {
      fields: [practiceTests.folderId],
      references: [folders.id],
    }),
    questions: many(practiceTestQuestions),
  }),
);

export const practiceTestQuestionsRelations = relations(
  practiceTestQuestions,
  ({ one }) => ({
    practiceTest: one(practiceTests, {
      fields: [practiceTestQuestions.practiceTestId],
      references: [practiceTests.id],
    }),
  }),
);

export const insertUsersSchema = createInsertSchema(users);
export const insertPurchasesSchema = createInsertSchema(purchases);
export const insertQuestionAttemptsSchema =
  createInsertSchema(questionAttempts);
export const userActivityCountsInsertSchema =
  createInsertSchema(userActivityCounts);
export const insertFilesSchema = createInsertSchema(files);
export const insertStudySetsSchema = createInsertSchema(studySets);
export const insertFolderSchema = createInsertSchema(folders);
export const insertQuestionOptionsSchema = createInsertSchema(questionOptions);
export const insertQuestionsSchema = createInsertSchema(questions);
export const insertFeedbackSchema = createInsertSchema(feedback);
export const insertAudioLessonsSchema = createInsertSchema(audioLessons);
